import React, { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Toast } from "../components/Toast";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const EliminarCuenta: React.FC = () => {
  const [formData, setFormData] = useState({
    email: "",
    reason: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    message: string;
    isVisible: boolean;
  }>({
    type: 'success',
    message: '',
    isVisible: false
  });

  // Validación
  const isPhoneValid = formData.email.length === 10 && /^\d+$/.test(formData.email);
  const isReasonValid = formData.reason.length >= 4;
  const isFormValid = isPhoneValid && isReasonValid;

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message, isVisible: true });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    const requestData = {
      phone: formData.email,
      reason: formData.reason,
    };

    console.log("🚀 Enviando request:", {
      url: `${API_BASE_URL}/api/v1/clients/contact`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: requestData
    });

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/clients/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      console.log("📡 Response status:", response.status);
      console.log("📡 Response headers:", response.headers);

      const responseText = await response.text();
      console.log("📡 Response body:", responseText);

      let responseData;
      try {
        responseData = JSON.parse(responseText);
        console.log("📡 Parsed response:", responseData);
      } catch {
        console.log("📡 Response is not JSON");
      }

      if (response.ok) {
        showToast('success', 'Solicitud enviada exitosamente. Procesaremos tu solicitud en un plazo máximo de 30 días.');
        setFormData({ email: "", reason: "" });
      } else {
        console.error("❌ Error response:", {
          status: response.status,
          statusText: response.statusText,
          body: responseData || responseText
        });
        showToast('error', `Error ${response.status}: ${responseData?.message || 'Por favor intenta de nuevo.'}`);
      }
    } catch (error) {
      console.error("❌ Network error:", error);
      showToast('error', 'Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="h-screen bg-salbox-neutral-50 flex flex-col">
      <Navbar />

      <main className="flex-1 flex items-center justify-center px-4 py-8 pt-24">
        <div className="bg-white rounded-2xl shadow-xl border border-salbox-neutral-200 p-6 md:p-8 w-full max-w-2xl">
          <h1 className="text-2xl md:text-3xl font-lufga-semibold text-salbox-neutral-900 mb-4 text-center">
            Eliminar cuenta y datos personales
          </h1>

          <div className="mb-6 text-center">
            <p className="text-salbox-neutral-700 mb-2 font-lufga">
              En Salbox respetamos tu privacidad.
            </p>

            <p className="text-salbox-neutral-700 text-sm font-lufga">
              Completa el formulario. Procesaremos tu solicitud en máximo 30
              días.
            </p>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-lufga-medium text-salbox-neutral-700 mb-2"
                >
                  Teléfono con el que te registraste{" "}
                  <span className="text-red-500">*</span>
                  <span className="text-xs text-salbox-neutral-500 ml-2">(10 dígitos)</span>
                </label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={10}
                  className={`w-full px-4 py-2 border rounded-xl shadow-sm focus:outline-none font-lufga transition-all duration-200 ${
                    formData.email && !isPhoneValid
                      ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                      : 'border-salbox-neutral-300 focus:ring-2 focus:ring-salbox-primary focus:border-salbox-primary'
                  }`}
                  placeholder="1234567890"
                />
                {formData.email && !isPhoneValid && (
                  <p className="text-red-500 text-xs mt-1 font-lufga">
                    Debe ser un teléfono de 10 dígitos
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="reason"
                  className="block text-sm font-lufga-medium text-salbox-neutral-700 mb-2"
                >
                  Motivo{" "}
                  <span className="text-red-500">*</span>
                  <span className="text-xs text-salbox-neutral-500 ml-2">(mínimo 4 caracteres)</span>
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows={3}
                  value={formData.reason}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-xl shadow-sm focus:outline-none font-lufga transition-all duration-200 resize-none ${
                    formData.reason && !isReasonValid
                      ? 'border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                      : 'border-salbox-neutral-300 focus:ring-2 focus:ring-salbox-primary focus:border-salbox-primary'
                  }`}
                  placeholder="Comparte el motivo de tu solicitud (mínimo 4 caracteres)"
                />
                {formData.reason && !isReasonValid && (
                  <p className="text-red-500 text-xs mt-1 font-lufga">
                    El motivo debe tener al menos 4 caracteres
                  </p>
                )}
                <p className="text-xs text-salbox-neutral-500 mt-1 font-lufga">
                  {formData.reason.length}/4 caracteres mínimos
                </p>
              </div>

              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full py-2 px-6 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-200 font-lufga-semibold shadow-lg ${
                  !isFormValid || isSubmitting
                    ? 'bg-salbox-neutral-400 text-salbox-neutral-600 cursor-not-allowed'
                    : 'bg-salbox-primary text-white hover:bg-salbox-primary-600 hover:shadow-xl focus:ring-salbox-primary'
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enviando...
                  </div>
                ) : (
                  'Enviar solicitud'
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />

      <Toast
        type={toast.type}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};
